import { LANDING_ASSETS, LANDING_COLORS } from '../content/landingContent';

/** Shared brand tokens — landing page is the source of truth */
export const BRAND = {
  ...LANDING_COLORS,
  fontFamily: "'Cabin', 'Roboto', Arial, sans-serif",
  logoWhite: LANDING_ASSETS.logo.desktop.hero,
  logoDark: LANDING_ASSETS.logo.desktop.scrolled,
  logoMobileWhite: LANDING_ASSETS.logo.mobile.hero,
  logoMobileDark: LANDING_ASSETS.logo.mobile.scrolled,
};

const sharedTypography = {
  fontFamily: BRAND.fontFamily,
  fontSize: 14,
  h1: { fontWeight: 700 },
  h2: { fontWeight: 700 },
  h3: { fontWeight: 600 },
  h4: { fontWeight: 600 },
  h5: { fontWeight: 600 },
  h6: { fontWeight: 600 },
  button: { textTransform: 'none', fontWeight: 600 },
};

const sharedComponents = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: 8,
        boxShadow: 'none',
        fontWeight: 600,
        '&:hover': { boxShadow: 'none' },
      },
      contained: {
        backgroundColor: BRAND.purple,
        '&:hover': { backgroundColor: BRAND.purpleLight },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 6, fontWeight: 500 },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': { borderRadius: 8 },
      },
    },
  },
};

/** Build MUI theme options for light or dark mode */
export function createBrandTheme(mode = 'light') {
  const isDark = mode === 'dark';

  return {
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: BRAND.purple,
        light: BRAND.purpleLight,
        dark: BRAND.purpleDark,
      },
      secondary: {
        main: BRAND.badgeBorder,
        light: BRAND.formBorder,
        dark: BRAND.purpleDark,
      },
      success: {
        main: BRAND.green,
        light: '#3dd955',
        dark: '#089a1f',
      },
      error: {
        main: BRAND.redLink,
        light: '#ec7063',
        dark: '#b82221',
      },
      warning: {
        main: '#E57702',
        light: '#f4d03f',
        dark: '#c06502',
      },
      info: {
        main: '#6588DF',
        light: '#5dade2',
        dark: '#2980b9',
      },
      background: isDark
        ? { default: '#121218', paper: '#1e1e28' }
        : { default: '#fafafa', paper: '#ffffff' },
      text: isDark
        ? { primary: '#e8e8ec', secondary: '#a0a0b0' }
        : { primary: BRAND.textGray, secondary: BRAND.textMuted },
      divider: isDark ? '#3a3a48' : BRAND.borderLight,
    },
    typography: sharedTypography,
    shape: { borderRadius: 8 },
    components: {
      ...sharedComponents,
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: isDark ? '0 2px 12px rgba(0, 0, 0, 0.35)' : '0 2px 12px rgba(0, 0, 0, 0.06)',
            border: `1px solid ${isDark ? '#3a3a48' : BRAND.borderLight}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: isDark ? '0 2px 12px rgba(0, 0, 0, 0.35)' : '0 2px 12px rgba(0,0,0,0.06)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  };
}

/** Light theme defaults — backwards compatible export */
export const brandMuiTheme = createBrandTheme('light');

export default BRAND;
